/**
 * voice-v8 — Werkzeugkarten: Bilder: Bildsuche, Bildraster mit Leuchtkasten, erzeugtes Bild.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { type IconName, icon, iconButton } from "../icons.js";
import { absatz, baueKarte, kartenTitel, linkZeile, wertZeile } from "../karte.js";
import { proxiedImg, unwrapResult } from "./hilfe.js";

interface ImageSearchResult {
  query?: string;
  count?: number;
  images?: Array<{
    title: string;
    image_url: string;
    source_url: string;
    source?: string;
    thumbnail?: string;
  }>;
}

export function buildImageSearchCard(result: unknown): HTMLElement {
  const r = (result ?? {}) as ImageSearchResult;
  const el = baueKarte("image-search");
  el.appendChild(
    kartenTitel(`Bilder · ${r.query ?? ""} (${r.images?.length ?? 0})`),
  );
  el.appendChild(renderImageGrid(r.images ?? [], r.query));
  return el;
}

// ===========================================================================
// Image Grid + Lightbox (Pre.264 — sauber refactored)
// ===========================================================================

interface GridImage {
  title?: string;
  alt?: string;
  url?: string;
  image_url?: string;
  source_url?: string;
  source?: string;
  thumbnail?: string;
}

function renderImageGrid(images: GridImage[], query?: string): HTMLElement {
  // Wrap als Block (volle Breite) — fixt Layout-Bug wo "Weitere Bilder"
  // neben dem Grid landete weil parent-Container flex war.
  const wrap = document.createElement("div");
  // `--grid`: bewusster display:block-Override der geteilten .tool-image-wrap
  // (die für Einzelbilder flex-zentriert). Sonst kehrt der Layout-Bug zurück.
  wrap.className = "tool-image-wrap tool-image-wrap--grid";

  const grid = document.createElement("div");
  grid.className = "tool-image-grid";

  images.forEach((img, idx) => {
    grid.appendChild(buildImageTile(img, idx, images));
  });

  wrap.appendChild(grid);

  // "Weitere Bilder auf Google" Button in eigener Zeile darunter.
  if (query) {
    const row = document.createElement("div");
    row.className = "tool-image-more-row";
    const btn = iconButton("external", "Weitere Bilder", {
      href: `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(query)}`,
    });
    row.appendChild(btn);
    wrap.appendChild(row);
  }

  return wrap;
}

function buildImageTile(
  img: GridImage,
  idx: number,
  all: GridImage[],
): HTMLElement {
  // Tile = Button mit Bild. Source-Domain wird UNTER dem Bild als kleiner
  // Footer angezeigt (nicht overlay) — überlagert das Produktbild nicht.
  const tile = document.createElement("button");
  tile.type = "button";
  tile.className = "tool-image-grid-item";
  tile.title = img.title || img.source || "Bild öffnen";

  // Bild-Bereich (quadratisch).
  const imgBox = document.createElement("div");
  imgBox.className = "tool-image-box";

  const im = document.createElement("img");
  im.className = "tool-image-el";
  // 2026-07-15 (Martin, "Bilder werden nicht angezeigt"): die VOLLE Bild-URL
  // zuerst. Braves imgs.search.brave.com-Vorschauen liefern AUSSERHALB der
  // Brave-Suche IMMER 403 (an deren Session gebunden) — die volle Quelle laedt
  // bei den meisten Seiten direkt (gleiche Reihenfolge wie die Lightbox).
  im.src = proxiedImg(img.image_url || img.url || img.thumbnail || "", img.source_url);
  im.alt = img.title || img.source || "";
  im.loading = "lazy";
  im.referrerPolicy = "no-referrer";
  // Hotlink-geschuetzte Seiten (Stockfoto-CDNs) blocken das Einbetten -> kein
  // kaputtes Bild-Icon, sondern ein ruhiger Platzhalter mit Quelle. Die Kachel
  // bleibt anklickbar (Lightbox/Quelle).
  im.addEventListener("error", () => {
    if (imgBox.dataset.failed) return;
    imgBox.dataset.failed = "1";
    im.remove();
    imgBox.classList.add("tool-image-box--failed");
    const ph = document.createElement("span");
    ph.className = "tool-image-fallback";
    ph.textContent = img.source || img.title || "Bild öffnen";
    imgBox.appendChild(ph);
  });
  imgBox.appendChild(im);
  tile.appendChild(imgBox);

  // Footer mit Domain (kleiner Strich darüber).
  if (img.source) {
    const footer = document.createElement("div");
    footer.className = "tool-image-footer";
    const label = document.createElement("span");
    label.className = "tool-image-footer-label";
    label.textContent = img.source;
    footer.appendChild(label);
    tile.appendChild(footer);
  }

  // Pre.805 (#78): Hover-Effekt in CSS (.tool-image-tile:hover) statt JS.
  tile.addEventListener("click", (e) => {
    e.preventDefault();
    openLightbox(all, idx);
  });

  return tile;
}

function openLightbox(images: GridImage[], startIdx: number): void {
  let idx = Math.max(0, Math.min(images.length - 1, startIdx));

  // 2026-07-09 (Martin, "ohne Inline-Geschichten"): Lightbox-Styles komplett
  // in CSS-Klassen (style.css, Block "LIGHTBOX"). Kein Object.assign, kein
  // zur Laufzeit injiziertes <style> mehr. Overlay bleibt bewusst fest dunkel.
  const backdrop = document.createElement("div");
  backdrop.className = "img-grid-lightbox";

  const figure = document.createElement("figure");

  const im = document.createElement("img");
  im.referrerPolicy = "no-referrer";

  const caption = document.createElement("figcaption");

  const sourceLink = document.createElement("a");
  sourceLink.className = "tool-image-lightbox-source";
  sourceLink.target = "_blank";
  sourceLink.rel = "noopener noreferrer";

  const counter = document.createElement("span");
  counter.className = "img-grid-counter";

  figure.appendChild(im);
  figure.appendChild(caption);
  figure.appendChild(sourceLink);
  figure.appendChild(counter);
  backdrop.appendChild(figure);

  function render(): void {
    const img = images[idx];
    if (!img) return;
    im.src = proxiedImg(img.image_url || img.url || img.thumbnail || "", img.source_url);
    im.alt = img.title || img.source || "";
    caption.textContent = img.title || img.source || "";
    const src = img.source_url || img.image_url || img.url || "#";
    sourceLink.href = src;
    // Clean: kein Unicode-Pfeil. Icon-SVG + Label.
    sourceLink.replaceChildren();
    sourceLink.appendChild(icon("external", { size: 14 }));
    const lbl = document.createElement("span");
    lbl.textContent = img.source
      ? `Zur Quelle: ${img.source}`
      : "Quelle öffnen";
    sourceLink.appendChild(lbl);
    counter.textContent = `${idx + 1} / ${images.length}`;
  }

  function close(): void {
    document.removeEventListener("keydown", onKey);
    backdrop.remove();
  }
  function prev(): void {
    if (images.length <= 1) return;
    idx = (idx - 1 + images.length) % images.length;
    render();
  }
  function next(): void {
    if (images.length <= 1) return;
    idx = (idx + 1) % images.length;
    render();
  }
  function onKey(e: KeyboardEvent): void {
    if (e.key === "Escape") close();
    else if (e.key === "ArrowLeft") prev();
    else if (e.key === "ArrowRight") next();
  }

  backdrop.addEventListener("click", (e) => {
    // Nur wenn auf Backdrop selbst (nicht auf figure/img)
    if (e.target === backdrop) close();
  });
  document.addEventListener("keydown", onKey);

  function makeNavBtn(
    iconName: IconName,
    cls: string,
    aria: string,
    onClick: () => void,
  ): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `img-grid-btn ${cls}`;
    b.setAttribute("aria-label", aria);
    b.appendChild(icon(iconName, { size: 22, stroke: 2.5 }));
    b.addEventListener("click", onClick);
    return b;
  }
  backdrop.appendChild(
    makeNavBtn("close", "img-grid-close", "Schließen", close),
  );
  if (images.length > 1) {
    backdrop.appendChild(
      makeNavBtn("chevron-left", "img-grid-prev", "Vorheriges Bild", prev),
    );
    backdrop.appendChild(
      makeNavBtn("chevron-right", "img-grid-next", "Nächstes Bild", next),
    );
  }

  render();
  document.body.appendChild(backdrop);
}

// --- generate_image / edit_image ----------------------------------------
// Pre.170: Tool-Card mit echtem Bild-Preview statt JSON-Fallback. Macht
// die generierten Bilder direkt im Chat sichtbar (Click → Lightbox via
// img-Click-Handler im app.ts).

export function buildImageCard(toolName: string, result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte(toolName.replace("_", "-"));
  const url = (r.url ?? r.openedUrl) as string | undefined;
  // 2026-09-04 (Martin: „Bilderzeugung geht nicht mehr"):
  //
  //   Vier Aufrufe hintereinander kamen mit
  //     {"ok":false,"error":"Gemini-Response enthält kein Bild
  //      (möglicherweise vom Safety-Filter blockiert)"}
  //   zurueck — und jede Karte schrieb „Bild bearbeitet". Ohne Bild, ohne
  //   Grund. Ein Fehlschlag, der aussieht wie ein Erfolg, ist schlimmer als
  //   eine Fehlermeldung: Man sucht den Fehler bei sich.
  //
  //   (Der Grund war hier echt und nicht behebbar: Google lehnt geschuetzte
  //   Figuren ab. Aber genau das muss dastehen.)
  const fehler = (r.error ?? r.fehler) as string | undefined;
  if (!url && (r.ok === false || fehler)) {
    el.appendChild(
      kartenTitel(
        toolName === "edit_image" ? "Bild nicht bearbeitet" : "Bild nicht erstellt",
      ),
    );
        // Bestehende Variante nutzen statt eine neue Klasse zu erfinden.
    el.appendChild(absatz((fehler ?? "Das Bild konnte nicht erzeugt werden.")
      .toString()
      .slice(0, 300), "warn"));
    return el;
  }
  el.appendChild(
    kartenTitel(toolName === "edit_image" ? "Bild bearbeitet" : "Bild erstellt"),
  );
  if (url) {
    const link = document.createElement("a");
    link.href = String(url);
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.className = "tool-image-wrap";
    const img = document.createElement("img");
    img.className = "tool-image";
    img.src = String(url);
    img.alt = (r.caption as string | undefined) ?? "Generiertes Bild";
    img.loading = "lazy";
    link.appendChild(img);
    el.appendChild(link);
  }
  if (r.caption) wertZeile(el, "Caption", String(r.caption));
  if (typeof r.size === "number") {
    const kb = Math.round((r.size) / 1024);
    wertZeile(el, "Größe", `${kb} kB`);
  }
  if (r.mime) wertZeile(el, "Typ", String(r.mime));
  if (url) {
    el.appendChild(linkZeile(String(url), "Bild öffnen"));
  }
  return el;
}
